import BigNumber from 'bignumber.js';
import {
  FRACTION_DIGITS_TWO_DECIMAL,
  MetricValueType,
  PlanRevisionStatus,
  RelatedService
} from 'utils/constants';
import { MuiChipColor } from 'models';
import { styled, Theme } from '@mui/material/styles';
import { Button, Divider } from '@mui/material';
import {
  BusinessDayPeriodInterface,
  GetDeviceProductionPlanDetailResponse,
  MarketPeriodInterface,
  MonitoredResourceType,
  MonitoredResourceTypeLabel,
  ProductionPlanRevisionInterface,
  TimeSeriesInterface
} from 'features/dispatcher/dispatcherTypes';
import { ApexOptions } from 'apexcharts';
import {
  DeviceCharacteristicGroupDetailType,
  DeviceDetailInterface
} from 'features/eai/eaiTypes';

interface ApexChartDimensions {
  width: string;
  medium: number;
  small: number;
}

// =============================|| DISPATCHER UTILS ||============================== //

export type UseChartDataType = {
  chartSeries: ApexAxisChartSeries;
  chartOptions: ApexOptions;
};

export const dispatcherChartDimensions: ApexChartDimensions = {
  width: '100%',
  medium: 300,
  small: 200
};

export const StyledActionButton = styled(Button)(() => ({
  size: 'small',
  minWidth: 0,
  minHeight: 0,
  width: '36px',
  height: '34px',
  fontSize: '0.69rem',
  paddingLeft: 0,
  paddingRight: 0,
  textAlign: 'center'
}));

export const ButtonGroupButton = styled(Button)(() => ({
  paddingTop: '10px',
  paddingBottom: '10px'
}));

export const StyledDivider = styled(Divider)(({ theme }) => ({
  opacity: 1,
  borderColor:
    theme.palette.mode === 'dark'
      ? theme.palette.dark.light + 15
      : theme.palette.grey[200]
}));

export const getProductionPlanRevisionChipColor = (
  revisionStatus?: PlanRevisionStatus
): MuiChipColor => {
  switch (revisionStatus) {
    case PlanRevisionStatus.REJECTED:
      return 'error';
    case PlanRevisionStatus.DRAFT:
    case PlanRevisionStatus.ACCEPTED:
      return 'orange';
    case PlanRevisionStatus.PLANNED:
    case PlanRevisionStatus.PROPAGATED:
      return 'success';
    case PlanRevisionStatus.CONFIRMED:
      return 'warning';
    case PlanRevisionStatus.NEW:
      return 'default';
    default:
      return 'default';
  }
};

export const findDeviceProductionPlan = (
  productionPlanList: GetDeviceProductionPlanDetailResponse[] | undefined,
  deviceId: string | bigint
): GetDeviceProductionPlanDetailResponse | undefined =>
  productionPlanList?.find(
    (productionPlan) => productionPlan.deviceId === deviceId
  );

export const createNewProductionPlanRevision =
  (): ProductionPlanRevisionInterface => ({
    id: 0,
    revisionNumber: 0,
    closingTime: null,
    description: '',
    propagationStatus: PlanRevisionStatus.NEW,
    periods: [],
    meta: {
      createdAt: '',
      createdBy: '',
      version: 0,
      contextActions: []
    }
  });

export const getDataPointValueRounded = (value?: string | number): string =>
  typeof value !== 'undefined'
    ? BigNumber(value).toFixed(FRACTION_DIGITS_TWO_DECIMAL)
    : '';

export const getCommonChartOptions = (
  colors: string[],
  theme: Theme
): Partial<ApexOptions> => ({
  chart: {
    background: '0',
    toolbar: {
      offsetX: -30
    },
    zoom: {
      enabled: true,
      autoScaleYaxis: true,
      type: 'x',
      allowMouseWheelZoom: false
    }
  },
  colors,
  legend: {
    position: 'bottom'
  },
  dataLabels: {
    enabled: false
  },
  theme: {
    mode: theme.palette.mode
  },
  grid: {
    show: true,
    borderColor:
      theme.palette.mode === 'dark'
        ? theme.palette.dark.main
        : theme.palette.grey['200']
  }
});

export const getTimeSeriesForMetricType = (
  timeSeriesList: TimeSeriesInterface[],
  metricType: MetricValueType
) => {
  return timeSeriesList.find(
    (timeSeriesItem) => timeSeriesItem.metric.type === metricType
  );
};

export const getTimeSeriesForDevice = (
  timeSeriesList: TimeSeriesInterface[],
  deviceId: string
) => {
  return timeSeriesList.filter((item) => {
    const monitoredResourceTypeIsDevice =
      item.monitoredResource.type === MonitoredResourceType.DEVICE;
    const monitoredResourceLabelIsForDeviceId =
      item.monitoredResource.labels?.some(
        (label) =>
          label.key === MonitoredResourceTypeLabel.DEVICE_ID &&
          label.value === deviceId
      );
    const metricIsForDeviceId = item.metric.labels?.some(
      (label) =>
        label.key === MonitoredResourceTypeLabel.DEVICE_ID &&
        label.value === deviceId
    );

    return (
      monitoredResourceTypeIsDevice &&
      (monitoredResourceLabelIsForDeviceId || metricIsForDeviceId)
    );
  });
};

export const getDeviceNominationForMarketPeriod = (
  productionPlans: GetDeviceProductionPlanDetailResponse[],
  deviceId: string,
  marketPeriod: MarketPeriodInterface
): BusinessDayPeriodInterface | undefined => {
  const deviceProductionPlan = productionPlans.find(
    (plan) => plan.deviceId === deviceId
  );

  /* deviceProductionPlan is fetched with flag `includedInCurrentNomination` to get only nominated revision*/
  const nominatedRevision = deviceProductionPlan?.revisions.at(0);
  return nominatedRevision?.periods?.find(
    (revisionPeriod) =>
      revisionPeriod.periodNumber === marketPeriod.timeUnitNumber
  );
};

export const getCharacteristicGroupForDeviceAndService = (
  relatedService: RelatedService,
  characteristicGroupList: DeviceCharacteristicGroupDetailType[],
  deviceDetail?: DeviceDetailInterface
) => {
  const deviceCharacteristicGroupId =
    deviceDetail?.deviceCharacteristicGroups[0]?.deviceCharacteristicGroupId;

  if (!deviceCharacteristicGroupId) return;
  return characteristicGroupList?.find((item) => {
    return (
      item.deviceCharacteristicGroupId === deviceCharacteristicGroupId &&
      item.relatedService === relatedService
    );
  });
};
